import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Edit } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useEtsyOrders } from "@/hooks/useEtsyOrders";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";

interface Expense {
  id: string;
  description: string;
  amount: number;
  category: string;
  expense_date: string;
  notes?: string;
}

interface ExpenseManagerProps {
  onExpensesChange: (expenses: Expense[]) => void;
}

const EXPENSE_CATEGORIES = [
  "Marketing & Advertising",
  "Sponsored Ads",
  "Materials & Supplies",
  "Packaging",
  "Shipping Costs",
  "Tools & Equipment",
  "Software & Subscriptions",
  "Professional Services",
  "Other"
];

const ExpenseManager = ({ onExpensesChange }: ExpenseManagerProps) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [formData, setFormData] = useState({
    description: "",
    amount: "",
    category: "",
    expense_date: new Date().toISOString().split('T')[0],
    notes: ""
  });
  const { toast } = useToast();
  const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscriptionStatus();
  const { etsyOrders } = useEtsyOrders(hasActiveSubscription, isSubscriptionLoading);

  // Get the primary currency from Etsy orders
  const primaryCurrency = etsyOrders && etsyOrders.length > 0 ? 
    etsyOrders.reduce((acc, order) => {
      const currency = order.currencyCode || 'USD';
      acc[currency] = (acc[currency] || 0) + 1;
      return acc;
    }, {} as Record<string, number>) : { 'USD': 1 };

  const storeCurrency = Object.entries(primaryCurrency)
    .sort(([,a], [,b]) => b - a)[0]?.[0] || 'USD';

  // Helper function to format price with store currency
  const formatPrice = (amount: number) => {
    return `${storeCurrency} ${amount.toFixed(2)}`;
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  const fetchExpenses = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.error('No authenticated user found');
        return;
      }

      const { data, error } = await supabase
        .from('business_expenses')
        .select('*')
        .eq('user_id', user.id)
        .order('expense_date', { ascending: false });

      if (error) throw error;
      setExpenses(data || []);
      onExpensesChange(data || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast({
        title: "Error",
        description: "Failed to fetch expenses",
        variant: "destructive"
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.description || !formData.amount || !formData.category) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Error",
          description: "You must be logged in to add expenses",
          variant: "destructive"
        });
        return;
      }

      const expenseData = {
        description: formData.description,
        amount: parseFloat(formData.amount),
        category: formData.category,
        expense_date: formData.expense_date,
        notes: formData.notes || null,
        user_id: user.id
      };

      if (editingExpense) {
        const { error } = await supabase
          .from('business_expenses')
          .update(expenseData)
          .eq('id', editingExpense.id);

        if (error) throw error;
        toast({ title: "Success", description: "Expense updated successfully" });
      } else {
        const { error } = await supabase
          .from('business_expenses')
          .insert([expenseData]);

        if (error) throw error;
        toast({ title: "Success", description: "Expense added successfully" });
      }

      setFormData({
        description: "",
        amount: "",
        category: "",
        expense_date: new Date().toISOString().split('T')[0],
        notes: ""
      });
      setEditingExpense(null);
      setIsDialogOpen(false);
      fetchExpenses();
    } catch (error) {
      console.error('Error saving expense:', error);
      toast({
        title: "Error",
        description: "Failed to save expense",
        variant: "destructive"
      });
    }
  };

  const handleEdit = (expense: Expense) => {
    setEditingExpense(expense);
    setFormData({
      description: expense.description,
      amount: expense.amount.toString(),
      category: expense.category,
      expense_date: expense.expense_date,
      notes: expense.notes || ""
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('business_expenses')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast({ title: "Success", description: "Expense deleted successfully" });
      fetchExpenses();
    } catch (error) {
      console.error('Error deleting expense:', error);
      toast({
        title: "Error",
        description: "Failed to delete expense",
        variant: "destructive"
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle>Business Expenses</CardTitle>
            <p className="text-sm text-gray-500 mt-1">Currency: {storeCurrency}</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => {
                setEditingExpense(null);
                setFormData({
                  description: "",
                  amount: "",
                  category: "",
                  expense_date: new Date().toISOString().split('T')[0],
                  notes: ""
                });
              }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingExpense ? 'Edit' : 'Add'} Business Expense</DialogTitle>
                <p className="text-sm text-gray-500">Amount will be recorded in {storeCurrency}</p>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="description">Description *</Label>
                  <Input
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                    placeholder="e.g., Facebook Ads Campaign"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="amount">Amount ({storeCurrency}) *</Label>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPENSE_CATEGORIES.map(category => (
                        <SelectItem key={category} value={category}>{category}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="expense_date">Date *</Label>
                  <Input
                    id="expense_date"
                    type="date"
                    value={formData.expense_date}
                    onChange={(e) => setFormData({...formData, expense_date: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Additional notes..."
                  />
                </div>
                <Button type="submit" className="w-full">
                  {editingExpense ? 'Update' : 'Add'} Expense
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {expenses.map((expense) => (
            <div key={expense.id} className="flex justify-between items-center p-3 border rounded-lg">
              <div className="flex-1">
                <div className="font-medium">{expense.description}</div>
                <div className="text-sm text-gray-500">
                  {expense.category} • {new Date(expense.expense_date).toLocaleDateString()}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-red-600">-{formatPrice(expense.amount)}</span>
                <Button variant="outline" size="sm" onClick={() => handleEdit(expense)}>
                  <Edit className="h-3 w-3" />
                </Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(expense.id)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
          {expenses.length === 0 && (
            <div className="text-center text-gray-500 py-4">
              No expenses recorded yet. Add your first expense to track profitability.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default ExpenseManager;
